import { getGeneralLinkHref } from "@/app/(site)/_utils/misc"
import { GeneralLink } from "@/types"
import { PortableText } from "@portabletext/react"
import { PortableTextBlock } from "sanity"
import { ReactNode } from "react"
import StoryText from "../Storytext"

interface Renderers {
    block?: {
        normal?: (children: ReactNode) => ReactNode
        preamble?: (children: ReactNode) => ReactNode
    }
}

type Props = {
    content: PortableTextBlock[]
    render?: Renderers
    paragraphStyling?: string
}

const PortableTextRenderer: React.FC<Props> = ({ content, render }) => {
    const components = {
        block: {
            normal: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.normal) {
                    return render.block.normal(children)
                }

                return (
                    <StoryText size="size3" className="mb-24 last:mb-0">
                        {children}
                    </StoryText>
                )
            },
            preamble: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.preamble) {
                    return render.block.preamble(children)
                }
                return (
                    <StoryText size="size2" className="mb-24 last:mb-0">
                        {children}
                    </StoryText>
                )
            }
        },
        marks: {
            link: ({
                children,
                value
            }: {
                children?: React.ReactNode
                value?: GeneralLink
            }) => {
                const blank = !!value?.external
                const href = value ? getGeneralLinkHref(value) : undefined
                const target = blank ? "_blank" : undefined

                if (typeof href === "undefined") return children

                return (
                    <a
                        href={href}
                        target={target}
                        rel={blank ? "noopener noreferrer" : undefined}
                        className="underline hover:no-underline"
                    >
                        {children}
                    </a>
                )
            }
        },
        list: {
            bullet: ({ children }: { children?: React.ReactNode }) => (
                <StoryText asChild size="size3" className="mb-24 last:mb-0">
                    <ul className="list-disc space-y-2 pl-[1em]">{children}</ul>
                </StoryText>
            ),
            number: ({ children }: { children?: React.ReactNode }) => (
                <StoryText asChild size="size3" className="mb-24 last:mb-0">
                    <ol className="list-decimal space-y-2 pl-[1em]">{children}</ol>
                </StoryText>
            )
        },
        listItem: {
            bullet: ({ children }: { children?: React.ReactNode }) => (
                <li className="ml-4">{children}</li>
            ),
            number: ({ children }: { children?: React.ReactNode }) => (
                <li className="ml-4">{children}</li>
            )
        }
    }

    return <PortableText value={content} components={components} />
}

export default PortableTextRenderer
